/**
 * graphCalendar.js — v2
 *
 * Lectura del calendario de Outlook vía Microsoft Graph.
 *
 * La app pedía el permiso `Calendars.Read` desde el principio pero nunca lo
 * usaba: no había una sola llamada a /me/events ni a /me/calendarView. Aquí
 * empieza a usarse.
 *
 * Se usa `calendarView` y no `events` a propósito: `events` devuelve la serie
 * maestra de las reuniones periódicas (una entrada, sin fecha concreta),
 * mientras que `calendarView` expande las repeticiones en ocurrencias reales
 * dentro del rango pedido. Para una agenda, es lo que hace falta.
 */

const GRAPH = 'https://graph.microsoft.com/v1.0';

export class GraphError extends Error {
  constructor(mensaje, { status = null, code = null } = {}) {
    super(mensaje);
    this.name = 'GraphError';
    this.status = status;
    this.code = code;
  }

  get mensajeUsuario() {
    if (this.status === 401 || this.status === 403) {
      return 'No hay permiso para leer el calendario. Vuelve a iniciar sesión y acepta el acceso a Calendars.Read.';
    }
    if (this.status === 429) {
      return 'Microsoft está limitando las peticiones. Inténtalo en unos segundos.';
    }
    return this.message;
  }
}

async function pedir(ruta, token, { timeoutMs = 20000 } = {}) {
  // Graph devuelve los enlaces de paginación como URL absolutas.
  const url = /^https?:\/\//i.test(ruta) ? ruta : `${GRAPH}${ruta}`;

  const control = new AbortController();
  const temporizador = setTimeout(() => control.abort(), timeoutMs);

  try {
    const respuesta = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        // Devuelve las horas en la zona del usuario en vez de UTC.
        Prefer: 'outlook.timezone="' + Intl.DateTimeFormat().resolvedOptions().timeZone + '"'
      },
      signal: control.signal
    });

    if (!respuesta.ok) {
      let detalle = `HTTP ${respuesta.status}`;
      try {
        const cuerpo = await respuesta.json();
        detalle = (cuerpo.error && cuerpo.error.message) || detalle;
      } catch {
        /* la respuesta de error no era JSON */
      }
      throw new GraphError(detalle, { status: respuesta.status });
    }

    return respuesta.json();
  } catch (error) {
    clearTimeout(temporizador);
    if (error instanceof GraphError) throw error;
    if (error.name === 'AbortError') {
      throw new GraphError('El calendario tardó demasiado en responder', { code: 'TIMEOUT' });
    }
    throw new GraphError(`No se pudo consultar el calendario: ${error.message}`);
  } finally {
    clearTimeout(temporizador);
  }
}

/** Inicio y fin del día local, en ISO, para acotar la consulta. */
function limitesDelDia(fecha) {
  const inicio = new Date(fecha);
  inicio.setHours(0, 0, 0, 0);
  const fin = new Date(fecha);
  fin.setHours(23, 59, 59, 999);
  return { inicio: inicio.toISOString(), fin: fin.toISOString() };
}

/** Una reunión de Graph → la forma que usa la interfaz. */
function aReunion(evento) {
  const asistentes = (evento.attendees || [])
    .map(a => (a.emailAddress && (a.emailAddress.name || a.emailAddress.address)) || null)
    .filter(Boolean);

  // Las direcciones aparte del nombre: hacen falta para escribirles después.
  const asistentesCorreo = (evento.attendees || [])
    .map(a => (a.emailAddress && a.emailAddress.address) || null)
    .filter(Boolean);

  return {
    id: evento.id,
    // El iCalUId es el mismo identificador que Teams graba en el metadato de la
    // grabación: es lo que permite unir la cita con su vídeo y su transcripción.
    iCalUid: evento.iCalUId || null,
    titulo: evento.subject || '(sin asunto)',
    inicio: evento.start && evento.start.dateTime ? new Date(evento.start.dateTime) : null,
    fin: evento.end && evento.end.dateTime ? new Date(evento.end.dateTime) : null,
    todoElDia: Boolean(evento.isAllDay),
    organizador: (evento.organizer && evento.organizer.emailAddress &&
      (evento.organizer.emailAddress.name || evento.organizer.emailAddress.address)) || null,
    asistentes,
    asistentesCorreo,
    esEnLinea: Boolean(evento.isOnlineMeeting),
    enlace: (evento.onlineMeeting && evento.onlineMeeting.joinUrl) || evento.onlineMeetingUrl || null,
    ubicacion: (evento.location && evento.location.displayName) || null,
    cancelada: Boolean(evento.isCancelled),
    resumen: evento.bodyPreview || null,
    enlaceOutlook: evento.webLink || null
  };
}

const CAMPOS = [
  'id', 'iCalUId', 'subject', 'start', 'end', 'isAllDay', 'organizer', 'attendees',
  'isOnlineMeeting', 'onlineMeeting', 'onlineMeetingUrl', 'location', 'isCancelled',
  'bodyPreview', 'webLink'
].join(',');

/**
 * Reuniones de un día concreto.
 * @param {string} token - token de acceso de Graph
 * @param {Date} fecha
 */
export async function reunionesDelDia(token, fecha = new Date()) {
  const { inicio, fin } = limitesDelDia(fecha);
  const ruta = `/me/calendarView?startDateTime=${encodeURIComponent(inicio)}` +
    `&endDateTime=${encodeURIComponent(fin)}` +
    `&$select=${CAMPOS}&$orderby=start/dateTime&$top=50`;

  const datos = await pedir(ruta, token);
  return (datos.value || []).map(aReunion).filter(r => !r.cancelada);
}

/**
 * Reuniones ya celebradas, de más reciente a más antigua.
 * Es lo que alimenta el listado de "Recientes".
 *
 * Pagina: Graph devuelve como mucho 100 eventos por respuesta y el resto por
 * `@odata.nextLink`. Sin seguir ese enlace, quien tenga muchas reuniones deja
 * de ver las más antiguas del rango sin que nada lo advierta.
 *
 * @param {string} token
 * @param {number} dias - cuántos días hacia atrás mirar
 * @param {number} limite - tope de reuniones a traer
 */
export async function reunionesPasadas(token, dias = 90, limite = 250) {
  const ahora = new Date();
  const desde = new Date(ahora);
  desde.setDate(desde.getDate() - dias);
  desde.setHours(0, 0, 0, 0);

  let ruta = `/me/calendarView?startDateTime=${encodeURIComponent(desde.toISOString())}` +
    `&endDateTime=${encodeURIComponent(ahora.toISOString())}` +
    `&$select=${CAMPOS}&$orderby=start/dateTime desc&$top=100`;

  const reuniones = [];

  while (ruta && reuniones.length < limite) {
    const datos = await pedir(ruta, token);
    reuniones.push(...(datos.value || []).map(aReunion));
    ruta = datos['@odata.nextLink'] || null;
  }

  return reuniones
    .filter(r => !r.cancelada && r.inicio && r.inicio < ahora)
    .slice(0, limite);
}

/**
 * Reuniones de un intervalo, de la más temprana a la más tardía.
 *
 * Es lo que alimenta la agenda lateral. Se pide la semana entera de una vez y
 * no un día por clic: siete días de calendario caben de sobra en una respuesta,
 * así que cambiar de día pasa a ser filtrar lo que ya está — sin espera, y sin
 * una petición cada vez que alguien recorre la tira de días con el ratón.
 */
export async function reunionesEntre(token, desde, hasta, limite = 250) {
  let ruta = `/me/calendarView?startDateTime=${encodeURIComponent(desde.toISOString())}` +
    `&endDateTime=${encodeURIComponent(hasta.toISOString())}` +
    `&$select=${CAMPOS}&$orderby=start/dateTime&$top=100`;

  const reuniones = [];

  while (ruta && reuniones.length < limite) {
    const datos = await pedir(ruta, token);
    reuniones.push(...(datos.value || []).map(aReunion));
    ruta = datos['@odata.nextLink'] || null;
  }

  return reuniones.filter(r => !r.cancelada).slice(0, limite);
}

/** Reuniones que quedan por delante hoy. */
export async function proximasDeHoy(token) {
  const ahora = new Date();
  const reuniones = await reunionesDelDia(token, ahora);
  return reuniones.filter(r => r.fin && r.fin >= ahora);
}

const graphCalendar = {
  reunionesDelDia, reunionesEntre, reunionesPasadas, proximasDeHoy, GraphError
};
export default graphCalendar;
